import { Injectable, inject } from '@angular/core';

import { BookingFeeRateSdk } from '@libs/graphql/sdk/booking-fee-rate/booking-fee-rate.sdk';
import { VendorSdk } from '@libs/graphql/sdk/vendor/vendor.sdk';
import { PlatformSettingSdk } from '@libs/graphql/sdk/platform-setting/platform-setting.sdk';
import type { BookingFeeRatePoco, PlatformSettingPoco, VendorPoco } from '@libs/graphql/models';
import { buildAuditFields, newEntityGuid } from '@shared/utils/audit-fields';
import { localNowForDb } from '@shared/utils/local-time';
import { vendorFeePctFor } from '@shared/utils/platform-fees.util';

/**
 * Freezes each involved vendor's Trexa fee onto a booking when its invoice goes out
 * (`BookingFeeRate`) — the write side of `vendorFeePctFor`.
 *
 * The admin fee card promises it in as many words: "Bookings already invoiced keep the rate they
 * were invoiced at — this applies to new ones." Without a snapshot the payout card, the wallet and
 * the completion release all read the vendor's CURRENT rate, so moving a vendor from 10% to 0%
 * silently re-prices every booking they have ever been invoiced on.
 *
 * Best-effort by design: a booking whose snapshot fails still invoices, and every reader falls back
 * to the vendor's live rate — the same figure the snapshot would have held. Never overwrites a rate
 * already snapshotted for a vendor; that row is settled history.
 */
@Injectable({ providedIn: 'root' })
export class BookingFeeRateService {
  private readonly feeRateSdk = inject(BookingFeeRateSdk);
  private readonly vendorSdk = inject(VendorSdk);
  private readonly platformSettingSdk = inject(PlatformSettingSdk);

  /**
   * Snapshot the fee for every vendor earning on this booking (the reseller + each package owner).
   * Vendors already snapshotted are skipped, so re-sending an invoice can't re-price it.
   */
  async snapshot(bookingId: number, vendorIds: Array<number | null | undefined>, userId: number): Promise<void> {
    const ids = Array.from(new Set(vendorIds.filter((v): v is number => !!v)));
    if (!bookingId || !ids.length) return;
    try {
      const [existing, vendors, setting] = await Promise.all([
        this.loadExisting(bookingId),
        this.loadVendors(ids),
        this.loadSetting(),
      ]);
      const done = new Set(existing.map(e => e.vendorId));
      const now = localNowForDb();
      for (const vendorId of ids) {
        if (done.has(vendorId)) continue;      // already frozen — settled history
        const feeRate = vendorFeePctFor({
          vendor: vendors.find(v => v.id === vendorId) ?? null,
          setting,
        });
        const res = await this.feeRateSdk.save({
          id: 0,
          guid: newEntityGuid(),
          bookingId,
          vendorId,
          feeRate,
          snappedDate: now,
          ...buildAuditFields(userId, now),
        } as never);
        if (!res.success) throw new Error(res.error || res.message || 'fee snapshot failed');
      }
    } catch (err) {
      // The invoice matters more than the snapshot: readers fall back to the live rate.
      console.warn('booking fee-rate snapshot failed — payouts fall back to the live vendor rate', err);
    }
  }

  private async loadExisting(bookingId: number): Promise<BookingFeeRatePoco[]> {
    const res = await this.feeRateSdk.list(
      { bookingId: { eq: bookingId } }, [{ column: 'id', desc: false }],
      ['id', 'bookingId', 'vendorId', 'feeRate'], 1, 200,
    );
    return (res.data as BookingFeeRatePoco[]) ?? [];
  }

  private async loadVendors(ids: number[]): Promise<VendorPoco[]> {
    const res = await this.vendorSdk.list(
      { id: { in: ids } }, [{ column: 'id', desc: false }], ['id', 'feeRate'], 1, 500,
    );
    return (res.data as VendorPoco[]) ?? [];
  }

  private async loadSetting(): Promise<PlatformSettingPoco | null> {
    const res = await this.platformSettingSdk.list({}, [{ column: 'id', desc: false }], ['id', 'vendorFeePct'], 1, 1);
    return ((res.data as PlatformSettingPoco[]) ?? [])[0] ?? null;
  }
}
